// "Hold & start new" timer helpers (v1.5.90).
//
// One timer can be put on hold while a second one runs, e.g. pause work for client A
// to time a phone call for client B. The held timer is frozen (it does not keep
// counting) and comes back as paused once the new timer is saved or discarded.

export interface HeldTimer {
  accumulatedSeconds: number;
  timerStartTimestamp: string | null;
  startTime: string;
  selectedAccount: string;
  selectedProject: string;
  selectedCode: string;
  notes: string;
  isBillable: boolean;
}

/** Seconds on the clock: the running stretch (if any) plus what was accumulated before. */
export function elapsedSecondsAt(timerStart: number | null, accumulatedSeconds: number, now: number): number {
  return timerStart ? Math.floor((now - timerStart) / 1000) + accumulatedSeconds : accumulatedSeconds;
}

export function buildHeldTimer(
  t: {
    timerStart: number | null;
    accumulatedSeconds: number;
    timerStartTimestamp: string | null;
    startTime: string;
    selectedAccount: string;
    selectedProject: string;
    selectedCode: string;
    notes: string;
    isBillable: boolean;
  },
  now: number,
): HeldTimer {
  return {
    accumulatedSeconds: elapsedSecondsAt(t.timerStart, t.accumulatedSeconds, now),
    timerStartTimestamp: t.timerStartTimestamp,
    startTime: t.startTime,
    selectedAccount: t.selectedAccount,
    selectedProject: t.selectedProject,
    selectedCode: t.selectedCode,
    notes: t.notes,
    isBillable: t.isBillable,
  };
}

/** Only one timer on hold at a time, and never while editing an existing entry. */
export function canHoldTimer(s: {
  timerRunning: boolean;
  timerPaused: boolean;
  held: HeldTimer | null;
  editingId: string | null;
}): boolean {
  return (s.timerRunning || s.timerPaused) && !s.held && !s.editingId;
}

/** 44 -> "44m", 65 -> "1h 5m", 120 -> "2h". */
export function formatMinutesShort(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

/** Anything worth keeping across a refresh and showing in other tabs. */
export function hasPersistableTimer(s: {
  timerRunning: boolean;
  timerPaused: boolean;
  timerStopped: boolean;
  held: HeldTimer | null;
}): boolean {
  return s.timerRunning || s.timerPaused || s.timerStopped || !!s.held;
}
